import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { AuthContext, useMaterialUIController } from "context";
import EntitySearchPicker from "cruds/onboarding/components/EntitySearchPicker";
import CustomerOnboardingWizard from "cruds/onboarding/customer/CustomerOnboardingWizard";
import AddAddressWizard from "cruds/onboarding/add-address/AddAddressWizard";
import AddItemsWizard from "cruds/onboarding/add-items/AddItemsWizard";
import api, { installationMessage } from "services/installation-api";
import { getOnboardingThemeTokens, withAlpha } from "./themeTokens";
import { motion } from "framer-motion";
import { CircularProgress, Divider } from "@mui/material";
import ClaimHead from "./claim/ClaimHead";
import { CLAIM_STAGES, claimStageCopy, outcomeStage } from "./claim/claimTitles";
import { buildThresholdRows, isPopulated, missingRequiredThresholds, submittedThresholdRows } from "./claim/thresholds";
import ThresholdFields from "./claim/ThresholdFields";
import ContactAccessTable, { accessText } from "./claim/ContactAccessTable";
import AlertSettings, { selectedRecipients } from "./claim/AlertSettings";
import InstallationSummary from "./claim/InstallationSummary";
import { buildAccessRows, buildDestinations, buildIntents, installBlockers, todayIso } from "./claim/claimIntents";
import { applyFollowUps, completeReviewedInstallation, readProgress, writeProgress } from "./claim/finalizeInstallation";
import { CustomerProfileCard } from "./install/steps/CustomerSteps";
import { clearClaimCustomerInstall } from "./claim/useClaimCustomerInstall";

const entityIdOf = (entity) => Number(entity?.attributes?.entity_id ?? entity?.entity_id ?? 0);

// Six sections; alert settings are a sub-stage of the asset & device section.
const STEPS = [
  { key: "customer", section: 1, label: "Customer", stage: CLAIM_STAGES.CUSTOMER },
  { key: "address", section: 2, label: "Address", stage: CLAIM_STAGES.ADDRESS },
  { key: "contacts", section: 3, label: "Contacts & Device Access", stage: CLAIM_STAGES.CONTACTS },
  { key: "asset_device", section: 4, label: "Tank / Asset & Device", stage: CLAIM_STAGES.ASSET_DEVICE },
  { key: "alerts", section: 4, label: "Tank / Asset & Device", stage: CLAIM_STAGES.ALERTS },
  { key: "providers", section: 5, label: "Provider Sharing", stage: CLAIM_STAGES.PROVIDERS },
  { key: "review", section: 6, label: "Review & Confirm", stage: CLAIM_STAGES.REVIEW },
];
const STEP_INDEX = Object.fromEntries(STEPS.map((step, index) => [step.key, index]));
const EMBEDDED_STAGE = {
  address: CLAIM_STAGES.ADDRESS,
  items: CLAIM_STAGES.ASSET_DEVICE,
};

function savedSession(key) {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (
      !value.session_id ||
      typeof value.session_token !== "string" ||
      value.session_token.length !== 64 ||
      !value.claim_id
    ) {
      throw new Error("Saved installation session is malformed");
    }
    return value;
  } catch (error) {
    sessionStorage.removeItem(key);
    throw new Error(`Cannot restore installation session: ${error.message}`);
  }
}

// Local-only draft of the follow-up choices; the server draft schema holds the
// core install fields. Never stores tokens or accepted attestations.
const localDraftKey = (claimCode) => `rivio_device_installation_choices:${claimCode}`;

function readLocalDraft(claimCode) {
  try {
    const raw = sessionStorage.getItem(localDraftKey(claimCode));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalDraft(claimCode, value) {
  try {
    sessionStorage.setItem(localDraftKey(claimCode), JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

function clearLocalDraft(claimCode) {
  try {
    sessionStorage.removeItem(localDraftKey(claimCode));
  } catch {
    /* storage unavailable */
  }
}

function addressText(address) {
  if (!address) return "";
  if (address.formatted_address) return address.formatted_address;
  return [address.street_address1, address.street_address2, address.city, [address.state, address.postal_code].filter(Boolean).join(" ")]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function attributeLabel(row) {
  const meta = row.attribute || row.Attribute || {};
  return meta.label || meta.attribute_name || `Attribute ${row.attribute_id}`;
}

function attributeUnit(row) {
  const meta = row.attribute || row.Attribute || {};
  return meta.unit || meta.unit_name || meta.uom || "";
}

function draftThresholds(rows) {
  return rows.map((row) => ({
    parameter_id: Number(row.parameter_id),
    threshold_type_id: row.threshold_type_id ?? null,
    alert_type_id: row.alert_type_id ?? null,
    comparison_operator_id: row.comparison_operator_id ?? null,
    threshold_value: row.threshold_value == null ? "" : String(row.threshold_value),
    alert_enabled: Boolean(row.alert_enabled),
    event_enabled: Boolean(row.event_enabled),
  }));
}

function createdAddressId(response) {
  const body = response?.data ?? response;
  return Number(body?.attributes?.address_id ?? body?.address_id ?? body?.id ?? 0) || null;
}

function createdAssetIds(results) {
  return (results || [])
    .map((response) => {
      const body = response?.data ?? response;
      return Number(body?.attributes?.address_item_id ?? body?.address_item_id ?? body?.id ?? 0) || null;
    })
    .filter(Boolean);
}

export default function DeviceInstallationWizard({ claimCode, routing }) {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const storageKey = `rivio_device_installation:${claimCode}`;
  const mounted = useRef(true);
  const [session, setSession] = useState(null);
  const [overview, setOverview] = useState(null);
  const [termsVersion, setTermsVersion] = useState(null);
  const [entity, setEntity] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileRequest, setProfileRequest] = useState(0);
  const [addressId, setAddressId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [installationDate, setInstallationDate] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [editAsset, setEditAsset] = useState(false);
  const [thresholds, setThresholds] = useState([]);
  const [contactAccess, setContactAccess] = useState({});
  const [alerts, setAlerts] = useState({ enabled: false, recipientKeys: [] });
  const [providers, setProviders] = useState([]);
  const [providerIds, setProviderIds] = useState([]);
  const [accuracy, setAccuracy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");
  const [startError, setStartError] = useState("");
  const [result, setResult] = useState(null);
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState(null);
  const [followUpError, setFollowUpError] = useState("");
  const [creationFlow, setCreationFlow] = useState(null);
  const [embeddedStage, setEmbeddedStage] = useState(CLAIM_STAGES.CUSTOMER);
  const [draftSaved, setDraftSaved] = useState(false);
  const submitLock = useRef(false);
  const pendingDraft = useRef(null);
  const pendingSelection = useRef(null);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = getOnboardingThemeTokens({ darkMode });
  const deviceId = overview?.device?.device_id ?? summary?.snapshot?.device_id ?? result?.device_id ?? null;

  async function loadInstallation(activeSession, isCurrent = () => mounted.current) {
    const [detail, stored, metaLookups, base, shareable] = await Promise.all([
      api.overview(activeSession),
      api.session(activeSession),
      api.loadThresholdLookups().catch(() => null),
      api.claimSetupOptions(activeSession).catch(() => null),
      api.shareOptions().catch(() => []),
    ]);
    if (!isCurrent()) return;
    const draft = stored.state?.installation_draft;
    const local = readLocalDraft(claimCode);
    pendingDraft.current = draft || null;
    setSession(activeSession);
    setOverview(detail);
    setTermsVersion(base?.lookups?.terms_version || null);
    setProviders(shareable || []);
    setDeviceName(draft?.device_name || detail.device?.name || "");
    setInstallationDate(draft?.installation_date || "");
    setAccuracy(false);
    setTerms(false);
    if (local) {
      setContactAccess(local.contactAccess || {});
      setAlerts(local.alerts || { enabled: false, recipientKeys: [] });
      setProviderIds(local.providerIds || []);
      if (local.addressId) pendingSelection.current = { addressId: String(local.addressId) };
    }
    if (draft?.entity_id) setEntity({ entity_id: draft.entity_id });
    setThresholds(
      buildThresholdRows({
        definitions: detail.threshold_definitions || [],
        existing: detail.thresholds || [],
        draft: draft?.thresholds || [],
        lookups: metaLookups,
      }),
    );
  }

  const loadSummary = useCallback(async (claimId) => {
    setResult({ claim_id: claimId });
    try {
      const data = await api.summary(claimId);
      if (!mounted.current) return;
      setSummary(data);
      setOperations(data?.operations || []);
    } catch (summaryError) {
      if (mounted.current) setFollowUpError(installationMessage(summaryError));
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    if (!auth.isAuthenticated) {
      setSession(null);
      setOverview(null);
      setEntity(null);
      setResult(null);
      setCreationFlow(null);
      setBusy(false);
      return () => {
        active = false;
        mounted.current = false;
      };
    }
    let activeSession;
    try {
      activeSession = savedSession(storageKey);
    } catch (restoreError) {
      setError(installationMessage(restoreError));
    }
    const progress = readProgress(claimCode);
    if (activeSession) {
      setRestoring(true);
      api
        .status(activeSession.claim_id)
        .then(async (status) => {
          if (!active) return;
          if (status.status === "completed") {
            sessionStorage.removeItem(storageKey);
            await loadSummary(status.claim_id || activeSession.claim_id);
          } else if (status.consumed_at || status.status !== "issued") {
            sessionStorage.removeItem(storageKey);
            setStartError("This installation claim is no longer available.");
          } else {
            await loadInstallation(activeSession, () => active);
          }
        })
        .catch((loadError) => {
          if (active) setStartError(installationMessage(loadError));
        })
        .finally(() => {
          if (active) setRestoring(false);
        });
    } else if (progress?.claimId) {
      // Completed here earlier: show the recorded summary, never a new install.
      loadSummary(progress.claimId);
    }
    return () => {
      active = false;
      mounted.current = false;
    };
  }, [auth.isAuthenticated, storageKey]);

  // Customer-scoped profile: addresses, assets, contacts, current access.
  useEffect(() => {
    const customerId = entityIdOf(entity);
    if (!session || !customerId) {
      setProfile(null);
      return undefined;
    }
    let active = true;
    setProfile((current) => (current && entityIdOf(entity) === Number(current.customer?.entity_id) ? { ...current, refreshing: true } : { loading: true }));
    api
      .claimSetupOptions(session, { entityId: customerId })
      .then((data) => {
        if (!active) return;
        setProfile(data);
        if (data?.lookups?.terms_version) setTermsVersion(data.lookups.terms_version);
        const draft = pendingDraft.current;
        if (draft && Number(draft.entity_id) === customerId && draft.asset_id) {
          pendingDraft.current = null;
          const asset = (data.assets || []).find((row) => String(row.address_item_id) === String(draft.asset_id));
          if (!asset) {
            setError("The saved asset is no longer available. Select an authorized asset again.");
          } else {
            setAddressId(String(asset.address_id));
            selectAsset(asset, { identifier: draft.identifier, attributes: draft.attributes });
          }
        }
        const selection = pendingSelection.current;
        if (selection) {
          pendingSelection.current = null;
          if (selection.addressId && (data.addresses || []).some((row) => String(row.address_id) === selection.addressId)) {
            setAddressId(selection.addressId);
          }
          if (selection.assetIds?.length === 1) {
            const asset = (data.assets || []).find((row) => Number(row.address_item_id) === Number(selection.assetIds[0]));
            if (asset) {
              setAddressId(String(asset.address_id));
              selectAsset(asset);
            }
          }
        }
      })
      .catch((lookupError) => {
        if (active) setProfile({ error: installationMessage(lookupError) });
      });
    return () => {
      active = false;
    };
  }, [entity, session, profileRequest]);

  // Follow-up choices survive reload; attestations are never saved.
  useEffect(() => {
    if (!session) return;
    writeLocalDraft(claimCode, { contactAccess, alerts, providerIds, addressId });
  }, [session, claimCode, contactAccess, alerts, providerIds, addressId]);

  function chooseCustomer(next) {
    setEntity(next);
    setAddressId("");
    setAssetId("");
    setAttributes([]);
    setIdentifier("");
    setContactAccess({});
    setAlerts({ enabled: false, recipientKeys: [] });
    pendingDraft.current = null;
  }

  function selectAsset(asset, saved = {}) {
    setAssetId(asset ? String(asset.address_item_id) : "");
    setIdentifier(saved.identifier || asset?.map_identifier || "");
    const savedValues = new Map((saved.attributes || []).map((row) => [String(row.attribute_id), row]));
    setAttributes(
      (asset?.attributes || []).map((row) => ({
        attribute_id: row.attribute_id,
        value: String(savedValues.get(String(row.attribute_id))?.value ?? row.value ?? ""),
        label: attributeLabel(row),
        unit: attributeUnit(row),
      })),
    );
    setEditAsset(false);
  }

  async function start() {
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    setStartError("");
    try {
      const next = await api.start(claimCode);
      sessionStorage.setItem(storageKey, JSON.stringify(next));
      await loadInstallation(next);
    } catch (failure) {
      setStartError(installationMessage(failure));
    } finally {
      submitLock.current = false;
      setBusy(false);
    }
  }

  async function saveDraft() {
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    setDraftSaved(false);
    setError("");
    try {
      await api.saveStep(session, "installation_draft", {
        action: "confirm",
        data: {
          entity_id: entityIdOf(entity) || null,
          asset_id: assetId || null,
          device_name: deviceName,
          identifier,
          installation_date: installationDate,
          attributes: attributes.map(({ attribute_id, value }) => ({ attribute_id, value })),
          thresholds: draftThresholds(thresholds),
        },
      });
      setDraftSaved(true);
    } catch (saveError) {
      setError(installationMessage(saveError));
    } finally {
      submitLock.current = false;
      setBusy(false);
    }
  }

  // Return from an embedded location/asset flow with the new record selected.
  function createdTarget({ entityId, addressId: newAddressId, response, results }) {
    if (!Number.isSafeInteger(Number(entityId)) || Number(entityId) < 1) {
      throw new Error("Creation succeeded but did not return a canonical customer entity ID.");
    }
    const flow = creationFlow;
    setCreationFlow(null);
    setError("");
    const selection = {};
    if (flow === "address") selection.addressId = String(createdAddressId(response) || "");
    if (flow === "items") {
      selection.addressId = newAddressId ? String(newAddressId) : "";
      selection.assetIds = createdAssetIds(results);
    }
    pendingSelection.current = selection;
    if (entityIdOf(entity) === Number(entityId)) {
      setProfileRequest((value) => value + 1);
    } else {
      setEntity({ entity_id: Number(entityId) });
    }
    setStepIndex(flow === "items" ? STEP_INDEX.asset_device : STEP_INDEX.address);
  }

  const runFollowUps = useCallback(async (claimId, plan) => {
    const outcome = await applyFollowUps({ api, claimId, operations: plan });
    if (!mounted.current) return;
    if (outcome.summary) setSummary(outcome.summary);
    setOperations(outcome.operations);
    setFollowUpError(outcome.error ? installationMessage(outcome.error) : "");
  }, []);

  // Shared by both paths once the core installation is confirmed.
  const installed = useCallback(async ({ completed, plan }) => {
    const claimId = completed?.claim_id || session?.claim_id;
    writeProgress(claimCode, { claimId });
    sessionStorage.removeItem(storageKey);
    clearLocalDraft(claimCode);
    clearClaimCustomerInstall(claimCode);
    setResult(completed || { claim_id: claimId });
    setCreationFlow(null);
    setSession(null);
    setOperations(plan.map((row) => ({ ...row, status: "pending" })));
    setSubmitting(false);
    await runFollowUps(claimId, plan);
  }, [claimCode, session, storageKey, runFollowUps]);

  const retryFollowUps = useCallback(async (failed) => {
    const claimId = summary?.snapshot?.claim_id || result?.claim_id || readProgress(claimCode)?.claimId;
    if (!claimId) return;
    setBusy(true);
    try {
      await runFollowUps(claimId, failed);
    } finally {
      setBusy(false);
    }
  }, [summary, result, claimCode, runFollowUps]);

  // ---- derived data for the existing-customer path ----
  const addresses = profile?.addresses || [];
  const assets = (profile?.assets || []).filter((row) => !addressId || String(row.address_id) === String(addressId));
  const selectedAddress = addresses.find((row) => String(row.address_id) === String(addressId)) ||
    (profile?.assets || []).find((row) => String(row.address_id) === String(addressId))?.address || null;
  const selectedAsset = (profile?.assets || []).find((row) => String(row.address_item_id) === String(assetId)) || null;
  const accessRows = useMemo(
    () => buildAccessRows({ profile, assetId, includeExisting: true, choices: contactAccess }),
    [profile, assetId, contactAccess],
  );
  const destinations = useMemo(() => buildDestinations({ profile, includeExisting: true }), [profile]);
  const recipients = selectedRecipients(alerts, destinations);
  const providerNames = providers.filter((row) => providerIds.includes(Number(row.provider_id))).map((row) => row.provider_name);
  const customer = profile?.customer || null;
  const assetDetails = attributes
    .filter((row) => String(row.value ?? "").trim())
    .map((row) => `${row.label} ${row.value}${row.unit ? ` ${row.unit}` : ""}`)
    .join("; ");

  const blockers = useMemo(() => {
    const list = installBlockers({
      deviceName,
      installationDate,
      thresholdsMissing: missingRequiredThresholds(thresholds),
      termsVersion,
      alerts,
      recipientCount: recipients.length,
      assetChosen: Boolean(selectedAsset),
    });
    if (!entityIdOf(entity)) list.unshift("Select the customer in Customer.");
    if (!addressId) list.splice(1, 0, "Select the service address in Address.");
    if (selectedAsset && !identifier.trim()) list.push("The asset identifier is required.");
    if (!accuracy || !terms) list.push("Confirm the information is accurate and accept the installation terms.");
    return list;
  }, [deviceName, installationDate, thresholds, termsVersion, alerts, recipients.length, selectedAsset, entity, addressId, identifier, accuracy, terms]);

  async function complete() {
    if (blockers.length || submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    setSubmitting(true);
    setError("");
    try {
      const outcome = await completeReviewedInstallation({
        api,
        session,
        target: { entityId: entityIdOf(entity), addressId: Number(addressId), assetId: Number(assetId) },
        install: {
          deviceName,
          installationDate,
          thresholds: submittedThresholdRows(thresholds),
          asset: {
            map_identifier: identifier.trim(),
            attributes: attributes
              .filter((row) => String(row.value).trim())
              .map(({ attribute_id, value }) => ({ attribute_id, value: String(value).trim() })),
          },
        },
        intents: buildIntents({ providerIds, accessRows, alerts, destinations }),
      });
      await installed(outcome);
    } catch (completionError) {
      // Nothing new was saved on this path: stay on review with the error.
      setError(installationMessage(completionError));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
      setBusy(false);
    }
  }

  // ---- single title owner for the whole claim page ----
  const step = STEPS[stepIndex] || STEPS[0];
  let stage;
  let reason;
  if (!auth.isAuthenticated) stage = CLAIM_STAGES.SIGN_IN;
  else if (result) {
    // Until the outcomes are known, the page reads as finishing, never complete.
    stage = operations ? outcomeStage({ completed: true, operations }) : CLAIM_STAGES.FINISHING;
  }
  else if (submitting) stage = CLAIM_STAGES.SUBMITTING;
  else if (restoring) stage = CLAIM_STAGES.LOOKUP;
  else if (creationFlow === "customer") stage = embeddedStage;
  else if (creationFlow) stage = EMBEDDED_STAGE[creationFlow];
  else if (!session && startError) stage = CLAIM_STAGES.ERROR;
  else if (!session && !routing.available) {
    stage = CLAIM_STAGES.UNAVAILABLE;
    reason = routing.unavailable_reason;
  } else if (!session) stage = CLAIM_STAGES.CUSTOMER;
  else stage = step.stage;
  const copy = claimStageCopy(stage, { reason });

  const panelSx = {
    background: t.panelBg,
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: "24px",
    border: `1px solid ${t.panelBorder}`,
    boxShadow: t.panelShadow,
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: darkMode ? "rgba(0,0,0,0.1)" : "#fff",
    },
  };
  const surfaceSx = { background: t.softSurface, border: `1px solid ${t.borderSubtle}` };

  const header = (subtitle) => (
    <motion.div initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.5 }}>
      <MDTypography variant="h3" component="h1" fontWeight="bold" mb={1} sx={{ color: t.textStrong }}>
        {copy.heading}
      </MDTypography>
      {subtitle ? (
        <MDTypography variant="body2" mb={3} sx={{ color: t.textMuted }}>
          {subtitle}
        </MDTypography>
      ) : null}
    </motion.div>
  );

  const go = (key) => {
    setStepIndex(STEP_INDEX[key]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const canAdvance = () => {
    if (step.key === "customer") return Boolean(entityIdOf(entity) && customer);
    if (step.key === "address") return Boolean(addressId);
    if (step.key === "asset_device") {
      return Boolean(selectedAsset && deviceName.trim() && installationDate && identifier.trim()) &&
        installationDate <= todayIso() && missingRequiredThresholds(thresholds).length === 0;
    }
    if (step.key === "alerts") return !alerts.enabled || recipients.length > 0;
    return true;
  };

  const reviewRow = (label, value) => (
    <MDBox display="flex" gap={2} mb={0.5} flexWrap="wrap">
      <MDTypography variant="body2" fontWeight="bold" sx={{ minWidth: 170, color: t.textStrong }}>
        {label}
      </MDTypography>
      <MDTypography variant="body2" sx={{ color: value ? t.textMuted : "error.main", fontWeight: value ? 400 : 700 }}>
        {value || "Required - missing"}
      </MDTypography>
    </MDBox>
  );

  const reviewBlock = (title, key, children) => (
    <MDBox p={2.5} borderRadius="16px" sx={surfaceSx}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="info">
          {title}
        </MDTypography>
        {key && (
          <MDButton variant="text" color="info" size="small" onClick={() => go(key)} disabled={busy}>
            Edit
          </MDButton>
        )}
      </MDBox>
      {children}
    </MDBox>
  );

  const renderStep = () => {
    switch (step.key) {
      case "customer":
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography variant="body2" sx={{ color: t.textMuted }}>
              Search by customer name, company, or ID. Choose New customer to create one with this device assignment.
            </MDTypography>
            <EntitySearchPicker selectedEntity={entity} onSelect={chooseCustomer} />
            {entityIdOf(entity) ? <CustomerProfileCard profile={profile} entity={entity} t={t} /> : null}
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                disabled={busy}
                onClick={() => {
                  setEmbeddedStage(CLAIM_STAGES.CUSTOMER);
                  setCreationFlow("customer");
                }}
                sx={{ borderRadius: "12px", textTransform: "none" }}
              >
                New customer
              </MDButton>
            </MDBox>
          </MDBox>
        );
      case "address":
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              label="Service address"
              value={addressId}
              disabled={busy || profile?.loading}
              onChange={(event) => {
                setAddressId(event.target.value);
                setAssetId("");
                setAttributes([]);
                setIdentifier("");
              }}
              fullWidth
              sx={{ ...inputSx, "& .MuiSelect-select": { minHeight: "3rem !important" } }}
            >
              {addresses.map((row) => (
                <MenuItem key={row.address_id} value={String(row.address_id)}>
                  {[row.name, addressText(row)].filter(Boolean).join(" - ") || `Address #${row.address_id}`}
                </MenuItem>
              ))}
            </TextField>
            {!addresses.length && !profile?.loading ? (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>This customer has no service address on file. Add one to continue.</Alert>
            ) : null}
            {selectedAddress ? (
              <MDTypography variant="body2" sx={{ color: t.textMuted }}>
                <strong>Property label:</strong> {selectedAddress.name || "None"} · <strong>Street address:</strong> {addressText(selectedAddress) || "Not recorded"}
              </MDTypography>
            ) : null}
            <MDBox>
              <MDButton variant="outlined" color="info" size="small" disabled={busy} onClick={() => setCreationFlow("address")} sx={{ borderRadius: "12px", textTransform: "none" }}>
                New location
              </MDButton>
            </MDBox>
          </MDBox>
        );
      case "contacts":
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography variant="body2" sx={{ color: t.textMuted }}>
              Choose which of this customer's contacts can access the device. Access is saved on the selected asset only;
              a contact's access to other assets is not changed.
            </MDTypography>
            {!assetId && (
              <Alert severity="info" sx={{ borderRadius: "12px" }}>
                Current access is shown once the asset is selected in the next step.
              </Alert>
            )}
            <ContactAccessTable
              rows={accessRows}
              onChange={(key, patch) => setContactAccess((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }))}
            />
          </MDBox>
        );
      case "asset_device":
        return (
          <MDBox display="flex" flexDirection="column" gap={3}>
            <TextField
              select
              label="Tank / asset at this address"
              value={assetId}
              disabled={busy || profile?.loading}
              onChange={(event) => selectAsset(assets.find((row) => String(row.address_item_id) === event.target.value))}
              fullWidth
              sx={{ ...inputSx, "& .MuiSelect-select": { minHeight: "3rem !important" } }}
            >
              {assets.map((row) => (
                <MenuItem key={row.address_item_id} value={String(row.address_item_id)}>
                  {row.name || row.map_identifier || `Asset ${row.address_item_id}`}
                  {row.item_name ? ` (${row.item_name})` : ""}
                  {row.map_identifier && row.name ? ` - ${row.map_identifier}` : ""}
                </MenuItem>
              ))}
            </TextField>
            {!assets.length && !profile?.loading ? (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>No assets at this address yet. Add one to continue.</Alert>
            ) : null}
            <MDBox>
              <MDButton variant="outlined" color="info" size="small" disabled={busy} onClick={() => setCreationFlow("items")} sx={{ borderRadius: "12px", textTransform: "none" }}>
                New asset
              </MDButton>
            </MDBox>
            {selectedAsset && !editAsset && (
              <MDBox p={2} borderRadius="16px" sx={surfaceSx}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="button" fontWeight="bold" sx={{ color: t.textStrong }}>
                    Saved asset details
                  </MDTypography>
                  <MDButton variant="text" color="info" size="small" onClick={() => setEditAsset(true)}>
                    Edit
                  </MDButton>
                </MDBox>
                <MDTypography variant="body2" sx={{ color: t.textMuted }}>
                  Identifier: {identifier || "Required - missing"}
                  {assetDetails ? ` · ${assetDetails}` : ""}
                </MDTypography>
              </MDBox>
            )}
            {selectedAsset && editAsset && (
              <MDBox display="flex" flexDirection="column" gap={2}>
                <TextField label="Asset identifier" value={identifier} onChange={(event) => setIdentifier(event.target.value)} fullWidth sx={inputSx} />
                {attributes.map((attribute, index) => (
                  <TextField
                    key={attribute.attribute_id}
                    label={`${attribute.label}${attribute.unit ? ` (${attribute.unit})` : ""}`}
                    value={attribute.value}
                    onChange={(event) =>
                      setAttributes((rows) => rows.map((row, i) => (i === index ? { ...row, value: event.target.value } : row)))
                    }
                    fullWidth
                    sx={inputSx}
                  />
                ))}
              </MDBox>
            )}
            <Divider sx={{ borderColor: t.divider }} />
            <TextField label="Device name" required value={deviceName} onChange={(event) => setDeviceName(event.target.value)} fullWidth sx={inputSx} />
            <TextField
              label="Installation date"
              type="date"
              required
              value={installationDate}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayIso() }}
              onChange={(event) => setInstallationDate(event.target.value)}
              fullWidth
              sx={inputSx}
            />
            <Divider sx={{ borderColor: t.divider }} />
            <MDTypography variant="button" fontWeight="bold" textTransform="uppercase" color="info">
              Thresholds
            </MDTypography>
            <ThresholdFields
              rows={thresholds}
              surfaceSx={surfaceSx}
              onChange={(index, patch) => setThresholds((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))}
            />
          </MDBox>
        );
      case "alerts":
        return <AlertSettings alerts={alerts} destinations={destinations} onChange={setAlerts} thresholds={thresholds} />;
      case "providers":
        return (
          <MDBox display="flex" flexDirection="column" gap={1}>
            <MDTypography variant="body2" sx={{ color: t.textMuted }}>
              Selecting a provider shares the device's assigned asset with that provider. Each share is saved after
              installation and shown as Shared only when the backend confirms it.
            </MDTypography>
            {providers.length === 0 ? (
              <MDTypography variant="body2" sx={{ color: t.textMuted }}>No shareable providers are available.</MDTypography>
            ) : (
              providers.map((provider) => (
                <FormControlLabel
                  key={provider.provider_id}
                  label={provider.provider_name}
                  control={
                    <Checkbox
                      checked={providerIds.includes(Number(provider.provider_id))}
                      onChange={(event) =>
                        setProviderIds((current) =>
                          event.target.checked
                            ? [...new Set([...current, Number(provider.provider_id)])]
                            : current.filter((id) => id !== Number(provider.provider_id)),
                        )
                      }
                    />
                  }
                />
              ))
            )}
          </MDBox>
        );
      case "review":
      default: {
        const populated = thresholds.filter((row) => isPopulated(row.threshold_value));
        return (
          <MDBox display="flex" flexDirection="column" gap={2}>
            {reviewBlock("Customer", "customer", (
              <>
                {reviewRow("Customer", customer?.display_name)}
                {reviewRow("Customer ID", customer?.entity_id ? String(customer.entity_id) : "")}
                {reviewRow("Entity type", customer?.entity_type)}
                {reviewRow("Company / Status", [customer?.company, customer?.status].filter(Boolean).join(" / "))}
              </>
            ))}
            {reviewBlock("Service address", "address", (
              <>
                {reviewRow("Property label", selectedAddress?.name || "None")}
                {reviewRow("Street address", addressText(selectedAddress))}
              </>
            ))}
            {reviewBlock("Contacts & device access", "contacts", (
              <ContactAccessTable readOnly rows={accessRows.filter((row) => row.enabled)} />
            ))}
            {reviewBlock("Tank / asset & device", "asset_device", (
              <>
                {reviewRow("Assigned asset", selectedAsset ? selectedAsset.name || selectedAsset.map_identifier || `Asset #${selectedAsset.address_item_id}` : "")}
                {reviewRow("Asset identifier", identifier)}
                {assetDetails ? reviewRow("Asset details", assetDetails) : null}
                {reviewRow("Device name", deviceName)}
                {reviewRow("Installation date", installationDate)}
                {reviewRow(
                  "Thresholds",
                  populated.length
                    ? populated.map((row) => `${row.parameter_label} ${row.threshold_value}${row.unit ? ` ${row.unit}` : ""} (alert ${row.alert_enabled ? "on" : "off"}, event ${row.event_enabled ? "on" : "off"})`).join("; ")
                    : "None configured",
                )}
              </>
            ))}
            {reviewBlock("Alert settings", "alerts", (
              <>
                {reviewRow("Alerts", alerts.enabled ? "Alerts on" : "Alerts off")}
                {alerts.enabled ? reviewRow("Recipients", recipients.map((row) => `${row.label} (${row.value})`).join("; ")) : null}
              </>
            ))}
            {reviewBlock("Provider sharing", "providers", (
              reviewRow("Providers", providerNames.length ? `${providerNames.join(", ")} - selected; saved status shown after submission` : "Not shared")
            ))}
            {reviewBlock("Device assignment", null, (
              <>
                {reviewRow("Device", deviceId ? `Device #${deviceId}` : "")}
                {reviewRow("Assignment", "Will be assigned")}
                {reviewRow(
                  "Target",
                  [customer?.display_name, addressText(selectedAddress), selectedAsset?.name || selectedAsset?.map_identifier].filter(Boolean).join(" -> "),
                )}
                {accessRows.some((row) => row.enabled)
                  ? reviewRow("Device access", accessRows.filter((row) => row.enabled).map((row) => `${row.name}: ${accessText(row)}`).join("; "))
                  : null}
              </>
            ))}
            <Alert
              severity="info"
              sx={{
                borderRadius: "12px",
                backgroundColor: t.primarySoft,
                color: t.textStrong,
                border: `1px dashed ${withAlpha(t.primary, darkMode ? 0.45 : 0.3)}`,
              }}
            >
              The existing plan and verified included term follow this device. The confirmed installation date anchors
              the term; future billing responsibility follows the backend subscription policy.
            </Alert>
            <MDBox display="flex" flexDirection="column" gap={1}>
              <FormControlLabel
                label="I confirm the customer, site, asset, and installation information is accurate"
                control={<Checkbox checked={accuracy} onChange={(event) => setAccuracy(event.target.checked)} />}
              />
              <FormControlLabel
                label={termsVersion ? `I accept the installation terms (version ${termsVersion})` : "Installation terms are loading…"}
                control={<Checkbox checked={terms} disabled={!termsVersion} onChange={(event) => setTerms(event.target.checked)} />}
              />
            </MDBox>
            {blockers.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: "12px" }}>
                {blockers.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </Alert>
            )}
          </MDBox>
        );
      }
    }
  };

  const renderContent = () => {
    if (!auth.isAuthenticated) {
      return (
        <MDBox component={motion.div} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} maxWidth="480px" width="90%" p={5} sx={{ ...panelSx, textAlign: "center" }}>
          <MDTypography variant="h4" component="h1" fontWeight="bold" mb={2} sx={{ color: t.textStrong }}>
            {copy.heading}
          </MDTypography>
          <MDTypography variant="body2" mb={4} sx={{ color: t.textMuted }}>
            You need to be signed in to continue with device installation.
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            onClick={() => {
              sessionStorage.setItem("device_claim_return_to", `/onboard/claim/${claimCode}`);
              navigate("/auth/login");
            }}
            sx={{ borderRadius: "12px", padding: "12px", textTransform: "none", boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)" }}
          >
            Sign in
          </MDButton>
        </MDBox>
      );
    }

    if (result) {
      return (
        <MDBox component={motion.div} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} maxWidth="960px" width="95%" p={{ xs: 3, md: 5 }} sx={panelSx}>
          {header(deviceId ? `Device #${deviceId}` : "")}
          {summary ? (
            <InstallationSummary summary={summary} operations={operations} onRetry={retryFollowUps} retrying={busy} error={followUpError} />
          ) : (
            <MDBox display="flex" flexDirection="column" gap={2}>
              <Alert severity={followUpError ? "warning" : "info"} sx={{ borderRadius: "12px" }}>
                {followUpError
                  ? `Installation saved. The summary could not be loaded: ${followUpError}`
                  : "Installation saved. Loading the confirmed summary…"}
              </Alert>
              {!followUpError && <CircularProgress size={24} />}
            </MDBox>
          )}
        </MDBox>
      );
    }

    if (creationFlow) {
      const onCancel = () => setCreationFlow(null);
      return (
        <MDBox component={motion.div} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }} maxWidth="960px" width="95%" p={4} sx={panelSx}>
          {header(deviceId ? `Device #${deviceId}` : "")}
          {creationFlow === "customer" && (
            <>
              <Alert severity="info" sx={{ borderRadius: "12px", mb: 3 }}>
                Review the customer, site, and asset below. Confirming will save the customer and assign Device #{deviceId} to the selected asset.
              </Alert>
              <CustomerOnboardingWizard
                onCancel={onCancel}
                claimContext={{
                  claimCode,
                  session,
                  deviceId,
                  initialDeviceName: deviceName || overview?.device?.name || "",
                  thresholds,
                  termsVersion,
                  api,
                  onStage: setEmbeddedStage,
                  onInstalled: installed,
                }}
              />
            </>
          )}
          {creationFlow === "address" && (
            <>
              <Alert severity="info" sx={{ borderRadius: "12px", mb: 3 }}>
                The installation session stays active. The new location is selected automatically when you return.
              </Alert>
              <AddAddressWizard onCancel={onCancel} onComplete={createdTarget} initialEntity={entity} />
            </>
          )}
          {creationFlow === "items" && (
            <>
              <Alert severity="info" sx={{ borderRadius: "12px", mb: 3 }}>
                The installation session stays active. A single new asset is selected automatically when you return.
              </Alert>
              <AddItemsWizard onCancel={onCancel} onComplete={createdTarget} initialEntity={entity} />
            </>
          )}
        </MDBox>
      );
    }

    if (!session) {
      return (
        <MDBox component={motion.div} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1, duration: 0.6, ease: "easeOut" }} maxWidth="760px" width="95%" p={{ xs: 3, md: 5 }} sx={panelSx}>
          {header(stage === CLAIM_STAGES.UNAVAILABLE
            ? routing.unavailable_reason === "completed"
              ? "This device has already been installed with this claim. Open it from Device claims to view the installation summary."
              : "This claim can no longer be used to install a device."
            : "Complete device installation by confirming the customer, site, asset, people, and settings.")}
          {startError && <Alert severity="error" sx={{ borderRadius: "12px", mb: 3 }}>{startError}</Alert>}
          {error && <Alert severity="error" sx={{ borderRadius: "12px", mb: 3 }}>{error}</Alert>}
          {routing.available && (
            <MDButton
              variant="gradient"
              color="info"
              size="large"
              disabled={busy || restoring}
              onClick={start}
              sx={{ borderRadius: "12px", padding: "12px 24px", textTransform: "none", boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)" }}
            >
              {busy || restoring ? (
                <MDBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} color="inherit" />
                  Loading...
                </MDBox>
              ) : (
                "Start installation"
              )}
            </MDButton>
          )}
        </MDBox>
      );
    }

    const isReview = step.key === "review";
    return (
      <MDBox component={motion.div} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1, duration: 0.6, ease: "easeOut" }} maxWidth="900px" width="95%" p={{ xs: 3, md: 5 }} sx={panelSx}>
        {header(`Step ${step.section} of 6: ${step.label}${deviceId ? ` · Device #${deviceId}` : ""}`)}
        {error && <Alert severity="error" sx={{ borderRadius: "12px", mb: 3 }}>{error}</Alert>}
        {draftSaved && (
          <Alert severity="success" sx={{ borderRadius: "12px", mb: 3 }}>
            Draft saved. Final confirmations must be checked again after resume.
          </Alert>
        )}
        {overview ? renderStep() : <CircularProgress size={24} />}
        <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" gap={2} mt={4}>
          <MDButton variant="outlined" color="secondary" disabled={busy || stepIndex === 0} onClick={() => go(STEPS[stepIndex - 1].key)} sx={{ borderRadius: "12px", textTransform: "none" }}>
            Back
          </MDButton>
          <MDBox display="flex" gap={2} flexWrap="wrap">
            <MDButton variant="outlined" color="info" disabled={busy} onClick={saveDraft} sx={{ borderRadius: "12px", textTransform: "none", minWidth: "140px" }}>
              Save draft
            </MDButton>
            {isReview ? (
              <MDButton
                variant="gradient"
                color="info"
                disabled={busy || blockers.length > 0}
                onClick={complete}
                sx={{ borderRadius: "12px", textTransform: "none", minWidth: "200px", boxShadow: "0 4px 14px 0 rgba(0, 118, 255, 0.39)" }}
              >
                {submitting ? (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} color="inherit" />
                    Saving installation...
                  </MDBox>
                ) : (
                  "Confirm & install device"
                )}
              </MDButton>
            ) : (
              <MDButton variant="gradient" color="info" disabled={busy || !canAdvance()} onClick={() => go(STEPS[stepIndex + 1].key)} sx={{ borderRadius: "12px", textTransform: "none", minWidth: "140px" }}>
                Next
              </MDButton>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <MDBox component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} display="flex" justifyContent="center" alignItems="center" minHeight="100vh" position="relative" overflow="hidden">
      <ClaimHead stage={stage} reason={reason} />
      <MDBox
        component={motion.div}
        animate={{ scale: [1, 1.15, 1], rotate: [0, 60, 0], x: [0, 40, 0], y: [0, -30, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        position="absolute"
        top="-10%"
        left="-10%"
        width="40vw"
        height="40vw"
        borderRadius="50%"
        sx={{ background: `radial-gradient(circle, ${withAlpha(t.primary, 0.12)} 0%, transparent 70%)`, filter: "blur(60px)", zIndex: 0 }}
      />
      <MDBox
        component={motion.div}
        animate={{ scale: [1, 1.2, 1], rotate: [0, -60, 0], x: [0, -40, 0], y: [0, 40, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
        position="absolute"
        bottom="-15%"
        right="-10%"
        width="50vw"
        height="50vw"
        borderRadius="50%"
        sx={{ background: `radial-gradient(circle, ${withAlpha(t.primary, 0.08)} 0%, transparent 70%)`, filter: "blur(80px)", zIndex: 0 }}
      />
      {renderContent()}
    </MDBox>
  );
}
